import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { createTemplate, updateTemplate, type PublishTemplate } from '@/api/playPublish';
import {
  type AppContent, type ContentRating, type ListingDefaults,
  readAppContent, readContentRating, readListingDefaults,
  validateAppContent, validateContentRating, validateListingDefaults, serializeListingDefaults, iarcCategoryFor,
} from '@/lib/play-template';
import {
  type DataSafetyForm, fromCsv, toCsv, validateDataSafety, defaultAdsAnalyticsDataSafety,
} from '@/lib/data-safety';
import { AppContentSection } from './template-editor/AppContentSection';
import { ContentRatingSection } from './template-editor/ContentRatingSection';
import { StoreSettingsSection } from './template-editor/StoreSettingsSection';
import { DataSafetySection } from './template-editor/DataSafetySection';
import { Field, Group, SectionTitle, Problems, Choice, YesNo, inputCls } from './template-editor/controls';

const TRACKS = ['production', 'beta', 'alpha', 'internal'] as const;
type Track = typeof TRACKS[number];

type TabId = 'general' | 'app_content' | 'content_rating' | 'store' | 'data_safety';
const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'general', label: 'General' },
  { id: 'app_content', label: 'App content' },
  { id: 'content_rating', label: 'Content rating' },
  { id: 'store', label: 'Store settings' },
  { id: 'data_safety', label: 'Data safety' },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** null = create a new template */
  template: PublishTemplate | null;
}

/**
 * Authoring surface for the questionnaire answers a job replays. Every answer
 * is a control, never raw JSON or CSV: the form only offers what the runner
 * can replay, and what it cannot is said next to the answer. Saving writes the
 * exact shapes the runner and the Android Publisher API read.
 */
export function PublishTemplateEditor({ isOpen, onClose, template }: Props) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabId>('general');
  const [name, setName] = useState('');
  const [track, setTrack] = useState<Track>('production');
  const [forHandoff, setForHandoff] = useState(false);
  const [appContent, setAppContent] = useState<AppContent>(() => readAppContent(null));
  const [contentRating, setContentRating] = useState<ContentRating>(() => readContentRating(null));
  const [listing, setListing] = useState<ListingDefaults>(() => readListingDefaults(null));
  const [dataSafety, setDataSafety] = useState<DataSafetyForm>(defaultAdsAnalyticsDataSafety);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTab('general');
    setName(template?.name ?? '');
    setTrack((TRACKS as readonly string[]).includes(template?.track ?? '') ? (template!.track as Track) : 'production');
    setForHandoff(!!template?.for_handoff);
    setAppContent(readAppContent(template?.app_content));
    setContentRating(readContentRating(template?.content_rating));
    setListing(readListingDefaults(template?.listing_defaults));
    setDataSafety(template?.data_safety_csv?.trim() ? fromCsv(template.data_safety_csv).form : defaultAdsAnalyticsDataSafety());
    setError(null);
  }, [isOpen, template]);

  // The IARC questionnaire follows the app type: Game for a game, "All other
  // app types" for an app. Switching the type switches it along.
  const changeAppContent = (next: AppContent) => {
    if (next.app_type !== appContent.app_type) {
      setContentRating((r) => ({ ...r, category: iarcCategoryFor(next.app_type) }));
    }
    setAppContent(next);
  };

  const problems = useMemo<Record<TabId, string[]>>(() => ({
    general: name.trim() ? [] : ['Name is required.'],
    app_content: validateAppContent(appContent),
    content_rating: validateContentRating(contentRating, appContent.app_type),
    store: validateListingDefaults(listing, appContent.app_type),
    data_safety: validateDataSafety(dataSafety),
  }), [name, appContent, contentRating, listing, dataSafety]);
  const problemCount = Object.values(problems).reduce((n, list) => n + list.length, 0);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        track,
        data_safety_csv: toCsv(dataSafety),
        content_rating: contentRating,
        app_content: appContent,
        listing_defaults: serializeListingDefaults(listing),
        for_handoff: forHandoff,
      };
      return template ? updateTemplate(template.id, payload) : createTemplate(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['publish-templates'] });
      onClose();
    },
    onError: (e: any) => setError(e?.message ?? 'Failed to save template'),
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={template ? `Edit template — ${template.name}` : 'New publish template'}
      size="5xl"
    >
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 sm:min-h-[60vh]">
        <nav className="sm:w-48 shrink-0 flex sm:flex-col gap-1 overflow-x-auto sm:overflow-visible -mx-1 px-1">
          {TABS.map((t) => {
            const active = t.id === tab;
            const count = problems[t.id].length;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm text-left whitespace-nowrap transition-colors
                  ${active ? 'bg-primary/10 text-primary font-medium' : 'text-content-muted hover:bg-surface-muted hover:text-content'}`}
              >
                <span>{t.label}</span>
                <span
                  className={`w-2 h-2 rounded-full shrink-0 ${count ? 'bg-red-400' : 'bg-emerald-400'}`}
                  title={count ? `${count} thing${count === 1 ? '' : 's'} to fix` : 'Ready'}
                />
              </button>
            );
          })}
        </nav>

        <div className="flex-1 min-w-0 space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400">{error}</div>
          )}
          <Problems items={problems[tab]} />

          {tab === 'general' && (
            <div className="space-y-4">
              <SectionTitle sub="One template per app archetype. Every job that uses it submits exactly these answers, which is what keeps them identical across accounts.">
                General
              </SectionTitle>
              <Group>
                <Field label="Name" hint="How the template shows up when creating a publication.">
                  <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Casino game · ads · 18+" />
                </Field>
                <Field label="Release track" hint="Production on a never-published app is sent for review by the runner; other tracks skip review.">
                  <Choice<Track> value={track} onChange={setTrack} options={TRACKS.map((t) => ({ value: t, label: t }))} />
                </Field>
                <Field
                  label="Default for share links"
                  hint="The handoff page shows the answers and Data safety CSV of the template the app was published with (its Play publish job or factory run). An app never queued for publishing gets this one instead. One template at a time: turning it on here turns it off on the other one."
                >
                  <YesNo value={forHandoff} onChange={setForHandoff} />
                </Field>
              </Group>
            </div>
          )}
          {tab === 'app_content' && <AppContentSection value={appContent} onChange={changeAppContent} />}
          {tab === 'content_rating' && <ContentRatingSection value={contentRating} appType={appContent.app_type} onChange={setContentRating} />}
          {tab === 'store' && <StoreSettingsSection value={listing} appType={appContent.app_type} onChange={setListing} />}
          {tab === 'data_safety' && <DataSafetySection value={dataSafety} onChange={setDataSafety} />}
        </div>
      </div>

      <div className="sticky bottom-0 -mb-4 pb-4 -mx-4 px-4 sm:-mx-6 sm:px-6 bg-surface flex items-center justify-between gap-3 mt-6 pt-4 border-t border-border">
        <span className="text-xs text-content-muted">
          {problemCount === 0
            ? 'Every answer can be replayed hands-off.'
            : `${problemCount} thing${problemCount === 1 ? '' : 's'} to fix before this template can run hands-off.`}
        </span>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={save.isPending} disabled={problemCount > 0} onClick={() => { setError(null); save.mutate(); }}>
            {template ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
